import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { GeneralService } from '../../../../services/general/general.service';

// Calendar
import {
  CalendarOptions,
  EventInput,
  EventSourceInput,
  FullCalendarComponent,
} from '@fullcalendar/angular'; // useful for typechecking
import esLocale from '@fullcalendar/core/locales/es';
import enLocale from '@fullcalendar/core/locales/en-gb';
import ruLocale from '@fullcalendar/core/locales/ru';
import moment from 'moment';
import { FormControl } from '@angular/forms';
import { DateTime } from 'luxon';
import { AgendaDialogComponent } from 'src/app/components/agenda-dialog/agenda-dialog.component';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';

@Component({
  selector: 'app-student-calendar-page',
  templateUrl: './calendar.component.html',
  styleUrls: ['../../../../../shared-styles/calendar.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CalendarComponent implements OnInit {
  @ViewChild('calendar') calendarComponent: FullCalendarComponent | undefined;
  @ViewChild('menu')
  menu!: ElementRef;

  public isMobile: any;
  public headerHeight: any = 0;
  public loading: any = false;
  public subscriptions: any = [];
  public timezoneCtrl = new FormControl();
  public events: any;

  // Calendar
  public calendarOptions: CalendarOptions = {};

  constructor(
    public general: GeneralService,
    private cdRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private dialog: MatDialog
  ) {
    this.isMobile = this.general.isMobile();

    this.timezoneCtrl.patchValue(this.general.getCurrentTimezone());

    this.calendarOptions = {
      initialView: this.isMobile ? 'timeGridDay' : 'timeGridWeek',
      views: {
        dayGridWeek: {},
        timeGridDay: {},
      },
      headerToolbar: {
        start: this.isMobile ? 'prev' : 'prev,next',
        center: 'title',
        end: this.isMobile ? 'next' : 'today timeGridWeek,timeGridDay',
      },
      height: 630,
      allDaySlot: false,
      slotLabelFormat: {
        hour: '2-digit',
        minute: this.isMobile ? undefined : '2-digit',
        omitZeroMinute: false,
        meridiem: false,
        hour12: false,
      },
      dayHeaderFormat: {
        weekday: this.isMobile ? 'long' : 'short',
        day: this.isMobile ? undefined : '2-digit',
      },
      locales: [esLocale, enLocale, ruLocale],
      locale:
        this.general.translate.currentLang === 'en'
          ? 'en-gb'
          : this.general.translate.currentLang,
      firstDay: 1,
      eventMinHeight: 30,
      eventClick: this.handleEventClick.bind(this),
      datesSet: this.handleDateSet.bind(this),
      timeZone: this.timezoneCtrl.value,
    };
  }

  async ngOnInit() {
    window.dispatchEvent(new CustomEvent('calendar-show-label'));
    window.dispatchEvent(new CustomEvent('show-side-nav'));
    document.addEventListener('set-lang', this);
    this.intializeSubscriptions();
  }

  ngOnDestroy() {
    document.removeEventListener('set-lang', this);
    this.subscriptions.forEach((subscription: any) => {
      subscription.unsubscribe();
    });
  }

  intializeSubscriptions() {
    this.subscriptions = [
      this.general.loading$.subscribe((res) => (this.loading = res)),
    ];
  }

  // Handle event Listener
  handleEvent(event: any) {
    switch (event.type) {
      case 'set-lang':
        const calendar = this.calendarComponent?.getApi();
        calendar?.setOption(
          'locale',
          event.detail === 'en' ? 'en-gb' : event.detail
        );
        break;
    }
  }

  /**
   * =================== Calendar ===================
   */
  handleEventClick(args: any) {
    const event = this.events.find((e: any) => e.id === args.event.id);
    if (event) {
      this.showDialogEvent(event);
    }
  }

  onGetHeight(height: any) {
    if (height) {
      this.headerHeight = height;
    }
  }
  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  async handleDateSet(event: any) {
    await this.getEvents(event.timeZone, event.startStr, event.endStr);
  }

  async getEvents(timezone: string, start: string, end: string) {
    try {
      await this.general.setLoading(true);

      const response = await this.general.httpGet('student-agenda', {
        timezone,
        start,
        end,
      });

      const calendar = this.calendarComponent?.getApi();
      this.events = [];

      const events: EventSourceInput = response.data.map((event: any) => {
        const end = DateTime.fromISO(event.end).setZone(timezone).toMillis();
        const now = DateTime.local().setZone(timezone).toMillis();

        if (now > end && event.state === '1') {
          event.state = '0';
        }
        this.events.push(event);

        const object: EventInput = {
          start: DateTime.fromISO(event.start).setZone(timezone).toString(),
          end: DateTime.fromISO(event.start).setZone(timezone).toString(),
          title: event.title,
          display: 'block',
          color: this.getColor(event),
          id: event.id,
        };
        return object;
      });
      this.calendarOptions.events = events;
      calendar?.refetchEvents();
    } catch (error) {
      console.log(error);
      await this.general.showErrorForm(error);
    } finally {
      await this.general.setLoading(false);
    }
  }

  getColor(event: any) {

    let color = '#2bb3bc';

    switch (event.type) {
      case 'single_class':
        color = '#2bb3bc';
        break;
      case 'google_calendar':
        color = '#6f757b';
        break;
      case 'test_class':
        color = '#8fc131';
        break;
      case 'weekly_class':
        color = '#6481d9';
        break;
      case 'group_class':
        color = '#6481d9';
        break;
      case 'no-available-time':
        color = '#fba358';
        break;
    }
    return color;
  }

  showDialogEvent(event: any) {
    const agendaDialogRef = this.dialog.open(AgendaDialogComponent, {
      data: event,
      autoFocus: false,
    });
  }
}
